import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  CheckCircle2,
  CreditCard,
  FileText,
  Loader2,
  Package,
  ReceiptText,
  Tag,
  User,
  Wallet,
  X
} from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { formatCurrency } from '../../utils/formatters';

const paymentStatusLabels = {
  Paid: 'Ödendi',
  PartiallyPaid: 'Kısmi Ödendi',
  Unpaid: 'Ödenmedi',
  Cancelled: 'İptal'
};

const paymentStatusTone = {
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  PartiallyPaid: 'bg-amber-50 text-amber-700 border-amber-100',
  Unpaid: 'bg-rose-50 text-rose-700 border-rose-100',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200'
};

const statusTone = {
  Active: 'bg-blue-50 text-blue-700 border-blue-100',
  Cancelled: 'bg-slate-100 text-slate-500 border-slate-200'
};

const DetailItem = ({ icon, label, value, hint, tone = 'default' }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
      {React.createElement(icon, { size: 14 })}
      {label}
    </div>
    {tone === 'badge' ? value : <div className="text-sm font-black text-slate-800">{value || '-'}</div>}
    {hint && <div className="mt-1 text-xs font-bold text-slate-400">{hint}</div>}
  </div>
);

const InvoiceDetailModal = ({ isOpen, onClose, invoiceId }) => {
  const navigate = useNavigate();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canEditInvoice = hasPermission('invoice', 'write');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [advances, setAdvances] = useState([]);
  const [advancesLoading, setAdvancesLoading] = useState(false);
  const [isAdvancePickerOpen, setIsAdvancePickerOpen] = useState(false);
  const [selectedAdvanceId, setSelectedAdvanceId] = useState('');
  const [allocatingAdvance, setAllocatingAdvance] = useState(false);
  const [advanceError, setAdvanceError] = useState('');

  const fetchInvoice = useCallback(async (signal) => {
    if (!invoiceId) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`/api/invoices/${invoiceId}`, { signal });
      setInvoice(response.data?.data || response.data || null);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        setError(err.response?.data?.message || 'Fatura detayı getirilemedi.');
        setInvoice(null);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [invoiceId]);

  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setInvoice(null);
      setError('');
      return undefined;
    }

    const controller = new AbortController();
    fetchInvoice(controller.signal);

    return () => controller.abort();
  }, [fetchInvoice, invoiceId, isOpen]);

  const accountId = invoice?.accountId?._id || invoice?.accountId || '';
  const invoiceDirection = ['Sales', 'SalesReturn'].includes(invoice?.type) ? 'Inflow' : 'Outflow';
  const loadAdvances = useCallback(async () => {
    if (!accountId || Number(invoice?.remainingAmount || 0) <= 0) {
      setAdvances([]);
      return;
    }

    setAdvancesLoading(true);
    setAdvanceError('');
    try {
      const response = await axios.get(`/api/accounts/${accountId}/unallocated-transactions`);
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      setAdvances(rows.filter((transaction) => transaction.type === invoiceDirection));
    } catch (err) {
      setAdvances([]);
      setAdvanceError(err.response?.data?.message || 'Serbest bakiyeler getirilemedi.');
    } finally {
      setAdvancesLoading(false);
    }
  }, [accountId, invoice?.remainingAmount, invoiceDirection]);

  useEffect(() => {
    if (!isOpen || !invoice?._id) return;
    loadAdvances();
  }, [invoice?._id, isOpen, loadAdvances]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const items = useMemo(() => Array.isArray(invoice?.items) ? invoice.items : [], [invoice]);
  const invoiceCurrency = invoice?.currency || 'TRY';
  const accountName = invoice?.accountId?.name || invoice?.accountName || '-';
  const accountCode = invoice?.accountId?.code || invoice?.accountCode || '';
  const operationLabel = invoice?.operation?.projectNo || invoice?.operation?.name || invoice?.operation?.code || '-';
  const paymentStatus = invoice?.paymentStatus || 'Unpaid';
  const status = invoice?.status || 'Active';
  const canUseAdvance = hasPermission('invoiceClearing', 'write') &&
    status !== 'Cancelled' && Number(invoice?.remainingAmount || 0) > 0 && advances.length > 0;

  const handleAllocateAdvance = async () => {
    if (!invoice?._id || !selectedAdvanceId) return;

    setAllocatingAdvance(true);
    setAdvanceError('');
    try {
      await axios.post(`/api/invoices/${invoice._id}/allocate-advance`, { transactionId: selectedAdvanceId });
      setIsAdvancePickerOpen(false);
      setSelectedAdvanceId('');
      await fetchInvoice();
      await loadAdvances();
    } catch (err) {
      setAdvanceError(err.response?.data?.message || 'Avans faturaya bağlanamadı.');
    } finally {
      setAllocatingAdvance(false);
    }
  };

  const handleEditClick = () => {
    if (!invoice?._id) return;
    onClose?.();
    navigate(`/invoices/${invoice._id}/edit`);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/20 bg-slate-50 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200/70 bg-white px-6 py-5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fatura Önizleme</p>
            <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">
              {invoice?.invoiceNumber || 'Fatura detayı'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Kapat"
          >
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {loading ? (
            <div className="flex min-h-80 flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 size={34} className="animate-spin text-blue-600" />
              <p className="text-xs font-black uppercase tracking-widest">Fatura yükleniyor</p>
            </div>
          ) : error ? (
            <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-3xl border border-rose-100 bg-rose-50 text-rose-600">
              <FileText size={34} />
              <p className="text-sm font-black">{error}</p>
            </div>
          ) : (
            <div className="space-y-6">
              <section>
                <div className="mb-3 flex items-center gap-2">
                  <ReceiptText size={18} className="text-blue-600" />
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Genel Bilgiler</h3>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem
                    icon={Tag}
                    label="Fatura No"
                    value={invoice?.invoiceNumber || '-'}
                    hint={invoice?.type || '-'}
                  />
                  <DetailItem
                    icon={CalendarDays}
                    label="Tarih / Vade"
                    value={invoice?.date ? new Date(invoice.date).toLocaleDateString('tr-TR') : '-'}
                    hint={invoice?.dueDate ? `Vade: ${new Date(invoice.dueDate).toLocaleDateString('tr-TR')}` : 'Vade yok'}
                  />
                  <DetailItem
                    icon={CheckCircle2}
                    label="Durum"
                    value={(
                      <span className={`inline-flex rounded-xl border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${paymentStatusTone[paymentStatus] || paymentStatusTone.Unpaid}`}>
                        {paymentStatusLabels[paymentStatus] || paymentStatus}
                      </span>
                    )}
                    tone="badge"
                    hint={statusTone[status] ? `Belge durumu: ${status}` : `Belge durumu: ${status}`}
                  />
                  <DetailItem
                    icon={Wallet}
                    label="Para Birimi"
                    value={invoiceCurrency}
                    hint={invoice?.exchangeRate && invoiceCurrency !== 'TRY' ? `Kur: ${invoice.exchangeRate}` : 'Kur: 1'}
                  />
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <User size={18} className="text-indigo-600" />
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Cari Bilgileri</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <DetailItem
                    icon={User}
                    label="Cari"
                    value={accountName}
                    hint={accountCode ? `Kod: ${accountCode}` : invoice?.accountId?._id || ''}
                  />
                  <DetailItem
                    icon={Package}
                    label="Operasyon"
                    value={operationLabel}
                    hint={invoice?.operation?.status || 'Bağlantı yok'}
                  />
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <CreditCard size={18} className="text-emerald-600" />
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Tahsilat Özeti</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <DetailItem icon={Wallet} label="Toplam" value={formatCurrency(invoice?.grandTotal || 0, invoiceCurrency)} />
                  <DetailItem icon={CreditCard} label="Ödenen" value={formatCurrency(invoice?.paidAmount || 0, invoiceCurrency)} />
                  <DetailItem icon={CheckCircle2} label="Kalan" value={formatCurrency(invoice?.remainingAmount || 0, invoiceCurrency)} />
                </div>

                {canUseAdvance && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                    <div>
                      <p className="text-sm font-black text-emerald-800">Kullanılabilir avans bulundu</p>
                      <p className="mt-1 text-xs font-semibold text-emerald-700">Cari hesabın serbest bakiyesini bu faturaya bağlayabilirsiniz.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAdvancePickerOpen((open) => !open)}
                      className="rounded-xl bg-emerald-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white hover:bg-emerald-700"
                    >
                      Avans / Serbest Bakiye Kullan
                    </button>
                  </div>
                )}

                {isAdvancePickerOpen && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-black text-slate-800">Kullanılacak avansı seçin</p>
                        <p className="mt-1 text-xs font-semibold text-slate-500">Faturanın kalan tutarı kadar otomatik mahsuplaştırılır.</p>
                      </div>
                      {advancesLoading && <Loader2 size={18} className="animate-spin text-slate-400" />}
                    </div>

                    <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
                      {advances.map((advance) => (
                        <label key={advance._id} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 ${selectedAdvanceId === String(advance._id) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-100'}`}>
                          <span className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="advanceTransaction"
                              checked={selectedAdvanceId === String(advance._id)}
                              onChange={() => setSelectedAdvanceId(String(advance._id))}
                              className="h-4 w-4 text-emerald-600"
                            />
                            <span>
                              <span className="block text-xs font-black text-slate-800">{advance.date ? new Date(advance.date).toLocaleDateString('tr-TR') : '-'}</span>
                              <span className="block text-[10px] font-bold text-slate-400">{advance.description || advance.category || 'Cari işlemi'}</span>
                            </span>
                          </span>
                          <span className="text-right">
                            <span className="block text-xs font-black text-slate-800">{formatCurrency(advance.amount || 0, advance.financialAccount?.currency || 'TRY')}</span>
                            <span className="block text-[10px] font-bold text-emerald-700">Serbest: {formatCurrency(advance.unallocatedAmount || 0, advance.financialAccount?.currency || 'TRY')}</span>
                          </span>
                        </label>
                      ))}
                    </div>

                    {advanceError && <p className="mt-3 text-xs font-bold text-rose-600">{advanceError}</p>}
                    <div className="mt-4 flex justify-end gap-2">
                      <button type="button" onClick={() => setIsAdvancePickerOpen(false)} className="rounded-xl px-4 py-2 text-xs font-black text-slate-500 hover:bg-slate-100">Vazgeç</button>
                      <button type="button" onClick={handleAllocateAdvance} disabled={!selectedAdvanceId || allocatingAdvance} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black text-white disabled:opacity-50">
                        {allocatingAdvance && <Loader2 size={14} className="animate-spin" />}
                        Avansı Bağla
                      </button>
                    </div>
                  </div>
                )}
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <FileText size={18} className="text-rose-600" />
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Kalemler</h3>
                </div>

                <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
                          <th className="p-4">Ürün / Hizmet</th>
                          <th className="p-4 text-right">Miktar</th>
                          <th className="p-4 text-right">Birim Fiyat</th>
                          <th className="p-4 text-right">KDV</th>
                          <th className="p-4 text-right">Toplam</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="py-14 text-center text-slate-400 font-bold">
                              Bu faturada kalem bulunmuyor.
                            </td>
                          </tr>
                        ) : items.map((item, index) => {
                          const itemName = item?.productId?.name || item?.productName || item?.name || item?.description || `Kalem ${index + 1}`;
                          const vatAmount = Number(item?.vatAmount || 0);
                          const vatRate = Number(item?.vatRate || 0);

                          return (
                            <tr key={item?._id || `${itemName}-${index}`} className="text-sm">
                              <td className="p-4">
                                <div className="font-black text-slate-800">{itemName}</div>
                                {item?.description && item.description !== itemName && (
                                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{item.description}</div>
                                )}
                              </td>
                              <td className="p-4 text-right font-black text-slate-700">{Number(item?.quantity || 0)}</td>
                              <td className="p-4 text-right font-black text-slate-700">
                                {formatCurrency(item?.unitPrice || 0, invoiceCurrency)}
                              </td>
                              <td className="p-4 text-right font-black text-slate-700">
                                <div>{vatRate > 0 ? `%${vatRate}` : '-'}</div>
                                <div className="text-[10px] font-bold text-slate-400">
                                  {vatAmount > 0 ? formatCurrency(vatAmount, invoiceCurrency) : '-'}
                                </div>
                              </td>
                              <td className="p-4 text-right font-black text-slate-900">
                                {formatCurrency(item?.grandTotal || item?.totalAmount || 0, invoiceCurrency)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {invoice?.notes && (
                <section className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Notlar</div>
                  <div className="whitespace-pre-wrap text-sm font-medium leading-6 text-slate-600">
                    {invoice.notes}
                  </div>
                </section>
              )}

              {canEditInvoice && (
                <div className="flex justify-end border-t border-slate-100 pt-2">
                  <button
                    type="button"
                    onClick={handleEditClick}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black"
                  >
                    Faturayı Düzenle
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
